import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import regionService from "../Services/Service";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import "../styles/Religion.css";

import {
  Button,
  Modal,
  Box,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import ModeEditOutlineRoundedIcon from "@mui/icons-material/ModeEditOutlineRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const RegionManager = () => {
  const regionRef = useRef(null);
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({
    id: "0",
    name: "",
  });

  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    regionService.setApiUrl(sessionStorage.getItem("APIUrl") + "/api/Region");

    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    const res = await regionService.getAll();
    setRegions(res.data);
  };

  // const clear = () => {
  //   setForm({
  //     id: "0",
  //     name: "",
  //   });

  //   setEditing(false);
  // };

  const clear = () => {
    setForm({
      id: "0",
      name: "",
    });

    if (regionRef.current) {
      regionRef.current.value = "";
    }

    setEditing(false);
  };

  const handleBlur = (e) => {
    const value = e.target.value.trim();

    if (!value) return;

    const found = regions.some(
      (r) => r.regionName.toLowerCase() === value.toLowerCase(),
    );

    if (found) {
      setSnackbar({
        open: true,
        message: "Duplicate Region!",
        severity: "error",
      });

      e.target.value = "";
    } else {
      setForm((prev) => ({
        ...prev,
        name: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const value = regionRef.current?.value.trim();

    // 1. Empty / whitespace-only validation
    if (!value) {
      setSnackbar({
        open: true,
        message: "Region name is required!",
        severity: "error",
      });
      return;
    }

    // 2. Only alphabets, spaces and hyphens allowed
    const regionNameRegex = /^[A-Za-z]+(?:[ -][A-Za-z]+)*$/;

    if (!regionNameRegex.test(value)) {
      setSnackbar({
        open: true,
        message: "Region name can contain only alphabets, spaces and hyphens!",
        severity: "error",
      });
      return;
    }

    // 3. Duplicate validation
    const found = regions.some(
      (r) =>
        r.regionName.toLowerCase() === value.toLowerCase() &&
        (!editing || r.regionId !== form.id),
    );

    if (found) {
      setSnackbar({
        open: true,
        message: "Duplicate Region!",
        severity: "error",
      });
      return;
    }

    // 4. Only now call API
    try {
      const response = await regionService.create({
        RegionId: form.id,
        RegionName: value,
        OpFlag: editing ? "Update" : "Insert",
        UserName: sessionStorage.getItem("userEmail"),
      });

      let vOutput = response.data.message.split(":");

      clear();
      fetchRegions();
      setOpen(false);

      setSnackbar({
        open: true,
        message: vOutput[0],
        severity: vOutput[1],
      });
    } catch (error) {
      console.error("Error saving region:", error);
    }
  };

  // const handleEdit = (row) => {
  //   setForm({
  //     id: row.regionId,
  //     name: row.regionName,
  //   });

  //   setEditing(true);
  //   setOpen(true);
  // };

  const handleEdit = (row) => {
    setForm({
      id: row.regionId,
      name: row.regionName,
    });

    if (regionRef.current) {
      regionRef.current.value = row.regionName;
    }

    setEditing(true);
    setOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    const response = await regionService.remove(deleteId, "Delete");

    let vOutput = response.data.message.split(":");

    fetchRegions();

    setSnackbar({
    open: true,
    message: vOutput[1],
    severity: vOutput[0],
  });
    setConfirmOpen(false);
  };

  const columns = [
    {
      field: "serialNo",
      headerName: "No.",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    {
      field: "regionId",
      headerName: "ID",
    },
    {
      field: "regionName",
      headerName: "Region Name",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            height: "100%",
          }}
        >
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<ModeEditOutlineRoundedIcon />}
            onClick={() => handleEdit(params.row)}
          >
            Edit
          </Button>

          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteRoundedIcon />}
            onClick={() => handleDelete(params.row.regionId)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="religion-grid-wrapper">
      <Box sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/DashboardFinal")}
          >
            Back To Home
          </Button>

          {/* <Button variant="contained" onClick={() => setOpen(true)}>
            Add Region
          </Button> */}

          <Button
            variant="contained"
            onClick={() => {
              clear();
              setOpen(true);
            }}
          >
            Add Region
          </Button>
        </Box>

        <div className="datagrid-container">
          <DataGrid
            rows={regions}
            columns={columns}
            columnVisibilityModel={{
              regionId: false,
            }}
            getRowId={(row) => row.regionId}
            components={{
              Toolbar: GridToolbar,
            }}
          />
        </div>
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            p: 3,
            bgcolor: "#fff",
            m: "auto",
            mt: 10,
            width: 400,
          }}
        >
          <h4>{editing ? "Edit" : "Add"} Region</h4>

          <form onSubmit={handleSubmit}>
            {/* <TextField
              label="Region Name"
              fullWidth
              required
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              onBlur={handleBlur}
            /> */}

            <TextField
              inputRef={regionRef}
              label="Region Name"
              fullWidth
              required
              defaultValue={form.name}
              onBlur={handleBlur}
            />

            <Box mt={2} textAlign="right">
              <Button
                onClick={() => {
                  setOpen(false);
                  clear();
                }}
              >
                Cancel
              </Button>

              <Button type="submit" variant="contained">
                Save
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this region?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>

          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={(event, reason) => {
          if (reason === "clickaway") {
            return;
          }

          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }));
        }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RegionManager;
